import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useParams, useNavigate } from "react-router-dom";
import {
  getTrafficCameras,
  getTrafficSignals,
  getEvents,
  getActions,
  createWorkflow,
  updateWorkflow,
  getWorkflowById,
} from "../../api";

const CreateEditWorkflowPage = () => {
  const { workflowId } = useParams(); // Get workflow ID from URL
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [workflowType, setWorkflowType] = useState("global");
  const [assetType, setAssetType] = useState(""); // For global workflows
  const [trafficCameras, setTrafficCameras] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]); // For local workflows
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrafficCameras();
    fetchTrafficSignals();
    fetchEvents();
    fetchActions();

    if (workflowId) {
      fetchWorkflow(); // Fetch workflow data for editing
    }
  }, [workflowId]);

  const fetchWorkflow = async () => {
    try {
      const response = await getWorkflowById(workflowId);
      const workflow = response.data;

      // Populate fields with existing workflow data
      setName(workflow.name);
      setDescription(workflow.description);
      setWorkflowType(workflow.type);
      setAssetType(workflow.assetType || ""); // For global workflows
      setSelectedAssets(
        workflow.type === "local"
          ? workflow.assets.map((asset) => asset.assetId)
          : []
      ); // For local workflows
      setSelectedEvents(workflow.events.map((event) => event._id));
      setSelectedActions(workflow.actions.map((action) => action._id));
    } catch (error) {
      console.error("Error fetching workflow:", error);
    }
  };

  const fetchTrafficCameras = async () => {
    try {
      const response = await getTrafficCameras({ limit: 100 });
      setTrafficCameras(response.data.data);
    } catch (error) {
      console.error("Error fetching traffic cameras:", error);
    }
  };

  const fetchTrafficSignals = async () => {
    try {
      const response = await getTrafficSignals({ limit: 100 });
      setTrafficCameras((prev) => [...prev, ...response.data.data]);
    } catch (error) {
      console.error("Error fetching traffic signals:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await getEvents({ limit: 100 });
      setEvents(response.data.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchActions = async () => {
    try {
      const response = await getActions({ limit: 100 });
      setActions(response.data.data);
    } catch (error) {
      console.error("Error fetching actions:", error);
    }
  };

  const isSaveButtonEnabled = () => {
    return (
      name.trim() !== "" &&
      description.trim() !== "" &&
      ((workflowType === "global" && assetType !== "") ||
        (workflowType === "local" && selectedAssets.length > 0)) &&
      selectedEvents.length > 0 &&
      selectedActions.length > 0
    );
  };

  const handleSaveWorkflow = async () => {
    try {
      const workflowData = {
        name,
        description,
        type: workflowType,
        assetType: workflowType === "global" ? assetType : undefined,
        assets:
          workflowType === "local"
            ? selectedAssets.map((id) => ({
                assetType: trafficCameras.find((camera) => camera._id === id)?.type || "TrafficCamera",
                assetId: id,
              }))
            : undefined,
        events: selectedEvents,
        actions: selectedActions,
      };

      if (workflowId) {
        await updateWorkflow(workflowId, workflowData);
      } else {
        await createWorkflow(workflowData);
      }

      navigate("/workflow");
    } catch (error) {
      console.error("Error saving workflow:", error);
    }
  };

  const steps = [
    "Workflow Information",
    "Select Assets",
    "Configure Events",
    "Configure Actions",
    "Review & Submit",
  ];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <TextField
              fullWidth
              label="Workflow Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
            />
            <FormControl component="fieldset" margin="normal">
              <Typography variant="subtitle1">Workflow Type</Typography>
              <RadioGroup
                value={workflowType}
                onChange={(e) => setWorkflowType(e.target.value)}
              >
                <FormControlLabel
                  value="global"
                  control={<Radio />}
                  label="Global Workflow (Applies to all assets of a type)"
                />
                <FormControlLabel
                  value="local"
                  control={<Radio />}
                  label="Local Workflow (Applies to specific locations/assets)"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        );
      case 1:
        return (
          <Box>
            {workflowType === "local" && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Assets</InputLabel>
                <Select
                  multiple
                  value={selectedAssets}
                  onChange={(e) => setSelectedAssets(e.target.value)}
                  input={<OutlinedInput label="Assets" />}
                  renderValue={(selected) =>
                    selected
                      .map((id) => trafficCameras.find((camera) => camera._id === id)?.["Location Name"])
                      .filter(Boolean)
                      .join(", ")
                  }
                >
                  {trafficCameras.map((asset, index) => (
                    <MenuItem key={`${asset._id}-${index}`} value={asset._id}>
                      <Checkbox checked={selectedAssets.includes(asset._id)} />
                      <ListItemText primary={asset["Location Name"]} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {workflowType === "global" && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Asset Type</InputLabel>
                <Select
                  value={assetType}
                  onChange={(e) => setAssetType(e.target.value)}
                  input={<OutlinedInput label="Asset Type" />}
                >
                  <MenuItem value="TrafficCamera">Traffic Camera</MenuItem>
                  <MenuItem value="TrafficSignal">Traffic Signal</MenuItem>
                  <MenuItem value="SpeedDetector">Speed Detector</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        );
      case 2:
        return (
          <FormControl fullWidth margin="normal">
            <InputLabel>Events</InputLabel>
            <Select
              multiple
              value={selectedEvents}
              onChange={(e) => setSelectedEvents(e.target.value)}
              input={<OutlinedInput label="Events" />}
              renderValue={(selected) =>
                selected
                  .map((id) => {
                    const event = events.find((event) => event._id === id);
                    return event ? `${event.type} (${event.description})` : "";
                  })
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {events.map((event) => (
                <MenuItem key={event._id} value={event._id}>
                  <Checkbox checked={selectedEvents.includes(event._id)} />
                  <ListItemText primary={`${event.type} (${event.description})`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 3:
        return (
          <FormControl fullWidth margin="normal">
            <InputLabel>Actions</InputLabel>
            <Select
              multiple
              value={selectedActions}
              onChange={(e) => setSelectedActions(e.target.value)}
              input={<OutlinedInput label="Actions" />}
              renderValue={(selected) =>
                selected
                  .map((id) => {
                    const action = actions.find((action) => action._id === id);
                    return action ? action.name : "";
                  })
                  .join(", ")
              }
            >
              {actions.map((action) => (
                <MenuItem key={action._id} value={action._id}>
                  <Checkbox checked={selectedActions.includes(action._id)} />
                  <ListItemText primary={action.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 4:
        return (
          <Box>
            <Typography variant="h6">Review Your Workflow</Typography>
            <Typography>Name: {name}</Typography>
            <Typography>Description: {description}</Typography>
            <Typography>Type: {workflowType}</Typography>
            {workflowType === "local" && (
              <Typography>
                Assets:{" "}
                {selectedAssets
                  .map((id) => trafficCameras.find((camera) => camera._id === id)?.["Location Name"])
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
            {workflowType === "global" && <Typography>Asset Type: {assetType}</Typography>}
            <Typography>
              Events:{" "}
              {selectedEvents
                .map((id) => events.find((event) => event._id === id)?.type)
                .filter(Boolean)
                .join(", ")}
            </Typography>
            <Typography>
              Actions:{" "}
              {selectedActions
                .map((id) => actions.find((action) => action._id === id)?.name)
                .join(", ")}
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 3 }}>
        <IconButton
          onClick={() => navigate("/workflow")}
          color="primary"
          sx={{ marginRight: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          {workflowId ? "Edit Workflow" : "Create New Workflow"}
        </Typography>
      </Box>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ marginTop: 2 }}>
        {renderStepContent(activeStep)}
        <Box sx={{ marginTop: 2 }}>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep((prev) => prev - 1)} sx={{ marginRight: 1 }}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 && (
            <Button variant="contained" onClick={() => setActiveStep((prev) => prev + 1)}>
              Next
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveWorkflow}
              disabled={!isSaveButtonEnabled()}
            >
              {workflowId ? "Update Workflow" : "Create Workflow"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CreateEditWorkflowPage;
